import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FaBookOpen,
  FaCheck,
  FaChevronRight,
  FaFilter,
  FaRedoAlt,
  FaSave,
  FaSchool,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../utils/api';
import { useApp } from '../../../context/AppContext';
import './enrollmentCollection.css';

const MEDIUMS = [
  { key: 'sindhi', label: 'Sindhi' },
  { key: 'urdu', label: 'Urdu' },
  { key: 'english', label: 'English' },
];
const FIELDS = [
  { key: 'total', label: 'Total', readOnly: true },
  { key: 'male', label: 'Male' },
  { key: 'female', label: 'Female' },
  { key: 'muslim', label: 'Muslim' },
  { key: 'nonMuslim', label: 'Non-Muslim' },
];

const emptyMedium = () => ({ total: 0, male: 0, female: 0, muslim: 0, nonMuslim: 0 });
const emptyMetrics = () => Object.fromEntries(MEDIUMS.map(({ key }) => [key, emptyMedium()]));
const listFrom = response => Array.isArray(response?.data) ? response.data : response?.data?.content || [];

function sortClasses(items) {
  return [...items].sort((a, b) =>
    String(a.name).localeCompare(String(b.name), undefined, { numeric: true, sensitivity: 'base' })
  );
}

export default function CollectedEnrollmentPage() {
  const { roles } = useApp();
  const isHeadMaster = roles?.includes('ROLE_HEAD_MASTER');
  const [options, setOptions] = useState({ regions: [], districts: [], talukas: [], schools: [], projects: [] });
  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState({ regionId: '', districtId: '', talukaId: '', schoolId: '', projectId: '' });
  const [activeClassId, setActiveClassId] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const selectedSchool = options.schools.find(item => String(item.id) === String(filters.schoolId));
  const selectedProject = options.projects.find(item => String(item.id) === String(filters.projectId));
  const ready = Boolean(filters.schoolId && filters.projectId);

  useEffect(() => {
    Promise.all([
      api.get('/api/v1/enrollment/region'),
      api.get('/api/v1/enrollment/school'),
      api.get('/api/v1/class'),
      api.get('/api/projects'),
    ]).then(([regions, schools, classRows, projects]) => {
      setOptions(current => ({
        ...current,
        regions: listFrom(regions).filter(item => item.is_active !== false),
        schools: isHeadMaster ? listFrom(schools).filter(item => item.is_active !== false) : current.schools,
        projects: listFrom(projects).filter(item => item.is_active !== false),
      }));
      const dbClasses = sortClasses(listFrom(classRows));
      setClasses(dbClasses);
      setActiveClassId(dbClasses[0]?.id || null);
      setValues(Object.fromEntries(dbClasses.map(item => [item.id, emptyMetrics()])));
    }).catch(() => Swal.fire('Unable to load page', 'Please check the API connection and try again.', 'error'))
      .finally(() => setLoading(false));
  }, [isHeadMaster]);

  const setFilter = (name, value) => {
    setFilters(current => {
      const next = { ...current, [name]: value };
      if (name === 'regionId') Object.assign(next, { districtId: '', talukaId: '', schoolId: '' });
      if (name === 'districtId') Object.assign(next, { talukaId: '', schoolId: '' });
      if (name === 'talukaId') next.schoolId = '';
      return next;
    });
  };

  useEffect(() => {
    if (!filters.regionId) {
      setOptions(current => ({ ...current, districts: [], talukas: [], schools: [] }));
      return;
    }
    api.get('/api/v1/enrollment/district', { params: { regionId: filters.regionId } })
      .then(response => setOptions(current => ({ ...current, districts: listFrom(response), talukas: [], schools: [] })));
  }, [filters.regionId]);

  useEffect(() => {
    if (!filters.districtId) return;
    api.get('/api/v1/enrollment/taluka', { params: { districtId: filters.districtId } })
      .then(response => setOptions(current => ({ ...current, talukas: listFrom(response), schools: [] })));
  }, [filters.districtId]);

  useEffect(() => {
    if (!filters.talukaId) return;
    api.get('/api/v1/enrollment/school', { params: { talukaId: filters.talukaId } })
      .then(response => setOptions(current => ({ ...current, schools: listFrom(response) })));
  }, [filters.talukaId]);

  const loadCollection = useCallback(async () => {
    if (!ready || !classes.length) return;
    setLoadingTable(true);
    try {
      const response = await api.get('/api/enrollment-collection', {
        params: { schoolId: filters.schoolId, projectId: filters.projectId },
      });
      const existing = Object.fromEntries(listFrom(response).map(row => [row.class_id, row.metrics]));
      setValues(Object.fromEntries(classes.map(item => [item.id, {
        ...emptyMetrics(),
        ...(existing[item.id] || {}),
      }])));
      setDirty(false);
    } catch {
      Swal.fire('Could not load enrollment', 'The selected collection could not be retrieved.', 'error');
    } finally {
      setLoadingTable(false);
    }
  }, [ready, classes, filters.schoolId, filters.projectId]);

  useEffect(() => { loadCollection(); }, [loadCollection]);

  const updateCell = (classId, medium, field, rawValue) => {
    const value = Math.max(0, Number(rawValue) || 0);
    setValues(current => {
      const classMetrics = { ...emptyMetrics(), ...(current[classId] || {}) };
      const mediumMetrics = { ...emptyMedium(), ...(classMetrics[medium] || {}), [field]: value };
      mediumMetrics.total = Math.max(
        Number(mediumMetrics.male) + Number(mediumMetrics.female),
        Number(mediumMetrics.muslim) + Number(mediumMetrics.nonMuslim)
      );
      return { ...current, [classId]: { ...classMetrics, [medium]: mediumMetrics } };
    });
    setDirty(true);
  };

  const resetAll = () => {
    setValues(Object.fromEntries(classes.map(item => [item.id, emptyMetrics()])));
    setDirty(true);
  };

  const save = async () => {
    if (!ready) return;
    setSaving(true);
    try {
      await api.post('/api/enrollment-collection/bulk', {
        schoolId: Number(filters.schoolId),
        projectId: Number(filters.projectId),
        entries: classes.map(item => ({ classId: item.id, metrics: values[item.id] || emptyMetrics() })),
      });
      setDirty(false);
      Swal.fire({ icon: 'success', title: 'Enrollment saved', text: 'The collection has been updated successfully.', timer: 1800, showConfirmButton: false });
    } catch {
      Swal.fire('Save failed', 'The enrollment collection could not be saved.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => classes.reduce((sum, item) => {
    const row = values[item.id] || {};
    return sum + MEDIUMS.reduce((mediumSum, medium) => mediumSum + Number(row[medium.key]?.total || 0), 0);
  }, 0), [classes, values]);

  if (loading) return <div className="enrollment-loading">Loading enrollment collection…</div>;

  return (
    <section className="enrollment-page">
      <header className="enrollment-heading">
        <div>
          <span className="enrollment-eyebrow"><FaBookOpen /> Enrollment management</span>
          <h1>Enrollment Collection</h1>
          <p>Collect class-wise enrollment by medium, gender and religion.</p>
        </div>
        <div className="enrollment-summary">
          <span>Current total</span>
          <strong>{totals.toLocaleString()}</strong>
          <small>{selectedProject?.name || 'Select a project'}</small>
        </div>
      </header>

      <div className="enrollment-filter-card">
        <div className="enrollment-filter-title">
          <span><FaFilter /> Collection filters</span>
          {ready && <em><FaCheck /> Ready to collect</em>}
        </div>
        <div className="enrollment-filters">
          {!isHeadMaster && <Filter label="Region" value={filters.regionId} onChange={value => setFilter('regionId', value)} options={options.regions} />}
          {!isHeadMaster && <Filter label="District" value={filters.districtId} onChange={value => setFilter('districtId', value)} options={options.districts} disabled={!filters.regionId} />}
          {!isHeadMaster && <Filter label="Taluka" value={filters.talukaId} onChange={value => setFilter('talukaId', value)} options={options.talukas} disabled={!filters.districtId} />}
          <Filter label="School" value={filters.schoolId} onChange={value => setFilter('schoolId', value)} options={options.schools} disabled={!isHeadMaster && !filters.talukaId} nameKey="school_name" />
          <Filter label="Project" value={filters.projectId} onChange={value => setFilter('projectId', value)} options={options.projects} />
        </div>
      </div>

      {!ready ? (
        <div className="enrollment-empty-state">
          <div><FaSchool /></div>
          <h2>Select a school and project</h2>
          <p>Use the filters above to open the class-wise enrollment collection table.</p>
        </div>
      ) : (
        <div className="enrollment-workspace">
          <aside className="enrollment-class-panel">
            <div className="enrollment-class-heading">
              <span>Classes</span>
              <b>{classes.length}</b>
            </div>
            <div className="enrollment-class-list">
              {classes.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={String(activeClassId) === String(item.id) ? 'active' : ''}
                  onClick={() => setActiveClassId(item.id)}
                >
                  <span>{item.name}</span><FaChevronRight />
                </button>
              ))}
            </div>
          </aside>

          <div className="enrollment-table-card">
            <div className="enrollment-table-context">
              <div><FaSchool /><span><b>{selectedSchool?.school_name || selectedSchool?.name}</b><small>{selectedSchool?.semis_code || 'School enrollment'}</small></span></div>
              <span className={dirty ? 'status dirty' : 'status'}>{dirty ? 'Unsaved changes' : 'All changes saved'}</span>
            </div>
            <div className="enrollment-table-scroll">
              {loadingTable ? <div className="enrollment-table-loading">Loading collection…</div> : (
                <table className="enrollment-grid">
                  <thead>
                    <tr>
                      <th rowSpan="2" className="class-column">Class</th>
                      {MEDIUMS.map(medium => <th key={medium.key} colSpan={FIELDS.length}>{medium.label} Medium</th>)}
                    </tr>
                    <tr>
                      {MEDIUMS.flatMap(medium => FIELDS.map(field => <th key={`${medium.key}-${field.key}`}>{field.label}</th>))}
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map(item => (
                      <tr key={item.id} className={String(activeClassId) === String(item.id) ? 'active-row' : ''}>
                        <th className="class-column">{item.name}</th>
                        {MEDIUMS.flatMap(medium => FIELDS.map(field => (
                          <td key={`${medium.key}-${field.key}`}>
                            <input
                              aria-label={`${item.name} ${medium.label} ${field.label}`}
                              type="number"
                              min="0"
                              value={values[item.id]?.[medium.key]?.[field.key] || 0}
                              readOnly={field.readOnly}
                              onChange={event => updateCell(item.id, medium.key, field.key, event.target.value)}
                              className={field.readOnly ? 'calculated' : ''}
                            />
                          </td>
                        )))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <footer className="enrollment-actions">
              <button type="button" className="reset-button" onClick={resetAll}><FaRedoAlt /> Reset all</button>
              <button type="button" className="save-button" onClick={save} disabled={saving || !dirty}><FaSave /> {saving ? 'Saving…' : 'Save enrollment'}</button>
            </footer>
          </div>
        </div>
      )}
    </section>
  );
}

function Filter({ label, value, onChange, options, disabled = false, nameKey = 'name' }) {
  return (
    <label className="enrollment-filter">
      <span>{label}</span>
      <select value={value} disabled={disabled} onChange={event => onChange(event.target.value)}>
        <option value="">Select {label.toLowerCase()}</option>
        {options.map(item => (
          <option key={item.id} value={item.id}>
            {item[nameKey] || item.name}{label === 'Project' && item.project_year ? ` · ${item.project_year}` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

Filter.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  disabled: PropTypes.bool,
  nameKey: PropTypes.string,
};
